/**
 * useWorkspaceInvitations Hook
 * 
 * Fetches workspace invitations (pending and declined) and subscribes to changes.
 */

import { useEffect, useState } from "react";
import type React from "react";
import { db } from "../../../firebase";
import {
  collection,
  getDocs,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { WorkspaceInvitation } from "../../../types";

export interface UseWorkspaceInvitationsParams {
  workspaceId: string;
}

export interface UseWorkspaceInvitationsResult {
  invitations: WorkspaceInvitation[];
  setInvitations: React.Dispatch<React.SetStateAction<WorkspaceInvitation[]>>;
  refresh: () => Promise<void>;
}

/**
 * Hook for fetching workspace invitations
 */
export const useWorkspaceInvitations = ({
  workspaceId,
}: UseWorkspaceInvitationsParams): UseWorkspaceInvitationsResult => {
  const [invitations, setInvitations] = useState<WorkspaceInvitation[]>([]);

  // Fetch invitations (pending and declined)
  const fetchInvitations = async (): Promise<void> => {
    try {
      const invitationsRef = collection(db, "workspaces", workspaceId, "invitations");
      // Fetch both pending and declined invitations
      const pendingQuery = query(invitationsRef, where("status", "==", "pending"));
      const declinedQuery = query(invitationsRef, where("status", "==", "declined"));
      
      const [pendingSnapshot, declinedSnapshot] = await Promise.all([
        getDocs(pendingQuery),
        getDocs(declinedQuery),
      ]);
      
      // Combine both snapshots
      const allInvitations = [...pendingSnapshot.docs, ...declinedSnapshot.docs];

      const invitationsList: WorkspaceInvitation[] = allInvitations.map((invDoc) => {
        const data = invDoc.data();
        return {
          id: invDoc.id,
          email: data.email as string,
          workspaceId: data.workspaceId as string,
          invitedBy: data.invitedBy as string,
          status: data.status as "pending" | "accepted" | "expired" | "declined",
          role: data.role as "admin" | "user" | "viewer" | undefined,
          createdAt: data.createdAt,
          expiresAt: data.expiresAt,
        };
      });

      setInvitations(invitationsList);
    } catch (error) {
      console.error("Error fetching invitations:", error);
    }
  };

  useEffect(() => {
    if (!workspaceId) return;

    // Initial fetch
    fetchInvitations();

    // Subscribe to invitations changes (both pending and declined)
    const invitationsRef = collection(db, "workspaces", workspaceId, "invitations");
    const unsubscribeInvitations = onSnapshot(invitationsRef, () => {
      fetchInvitations();
    });

    return () => {
      unsubscribeInvitations();
    };
  }, [workspaceId]);

  return {
    invitations,
    setInvitations,
    refresh: fetchInvitations,
  };
};
